import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams, useNavigate } from 'react-router'
import { Button, Alert, Spinner } from 'react-bootstrap'
import { getEnterPicks, enterPicksCommand, saveRegularSeasonPicks,
  findMyFootballMatchups, setPickTeamId } from '@severed-links/common.redherrings-reducers/football'
import { orderBy } from 'lodash'
import * as s from './EnterPicks.scss'
import EnterPicksGameItem from './EnterPicksGameItem'
import EnterPicksGameContainer from './EnterPicksGameContainer'
import moment from 'moment-timezone'

const EnterRegularSeasonFootballPicks = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const params = useParams()
  const playerId = useSelector(state => state.account._id)
  const seasons = useSelector(state => state.football.seasons)
  const seasonName = params.seasonName
  const week = useSelector(state => state.football.week)
  const myTeams = useSelector(state => state.football.myTeams) || []
  const pickTeamId = useSelector(state => state.football.pickTeamId)
  const pickDeadline = useSelector(state => state.football.pickDeadline)
  const picks = useSelector(state => state.football.picks) || {}
  const pickGameData = useSelector(state => state.football.pickGameData) || []
  const leagueScoreboards = useSelector(state => state.football.leagueScoreboards)
  const [isSaving, setSaving] = useState(false)
  const [isSavedOnce, setSavedOnce] = useState(false)
  // The shared reducer clears its flag even for HTTP-200 business errors.
  const [dirty, setDirty] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [saveError, setSaveError] = useState('')
  const [loadedKey, setLoadedKey] = useState(null)
  const [attempt, setAttempt] = useState(0)
  const [now, setNow] = useState(() => Date.now())
  const saving = useRef(false)
  const myTeam = myTeams.find(team => team._id === pickTeamId)
  const requestKey = `${seasonName}-${week}-${pickTeamId}`
  const isLoading = loadedKey !== requestKey && !loadError
  const deadline = picks.pickDeadline || pickDeadline
  const deadlineValid = !!deadline && moment(deadline).isValid()
  const afterDeadline = deadlineValid && moment(now).isSameOrAfter(deadline)
  const canEdit = !isLoading && !loadError && !!myTeam && deadlineValid && !afterDeadline && !isSaving
  const myGame = (findMyFootballMatchups({ leagueScoreboards, seasons, seasonName, week, playerId }) || [])
    .find(game => game._id1 === pickTeamId || game._id2 === pickTeamId)

  useEffect(() => {
    if (!myTeam && myTeams.length) dispatch(setPickTeamId(myTeams[0]._id))
  }, [pickTeamId, myTeams])

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 10000)
    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    const beforeUnload = event => {
      if (!dirty && !saving.current) return
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', beforeUnload)
    return () => window.removeEventListener('beforeunload', beforeUnload)
  }, [dirty])

  useEffect(() => {
    if (!seasonName || !week || !myTeam) return
    let active = true
    setLoadedKey(null)
    setLoadError('')
    setSaveError('')
    setDirty(false)
    setSavedOnce(false)
    const load = async () => {
      try {
        const result = await dispatch(getEnterPicks(seasonName, week, pickTeamId))
        if (result?.error || !Array.isArray(result?.payload?.items)) throw new Error(result?.payload?.message || 'Unable to load your picks. Please try again.')
        if (active) { setLoadedKey(requestKey); setNow(Date.now()) }
      } catch (error) {
        if (active) setLoadError(error.message || 'Unable to load your picks. Please try again.')
      }
    }
    load()
    return () => { active = false }
  }, [seasonName, week, myTeam?._id, attempt])

  const mayLeave = () => !saving.current && (!dirty || window.confirm('Discard your unsaved pick changes?'))
  const addCommand = command => {
    if (!canEdit || saving.current || moment().isSameOrAfter(deadline)) return
    if (command.type === 'changeRank' && command.oldRank === command.newRank) return
    if (command.type === 'makePick' && picks.items?.find(game => game.game === command.game)?.pick === command.pick) return
    dispatch(enterPicksCommand(command))
    setDirty(true)
    setSavedOnce(false)
    setSaveError('')
  }
  const savePicks = async () => {
    if (!canEdit || !dirty || saving.current) return
    if (moment().isSameOrAfter(deadline)) { setNow(Date.now()); return }
    saving.current = true
    setSaving(true)
    setSaveError('')
    try {
      const result = await dispatch(saveRegularSeasonPicks({
        teamId: pickTeamId,
        picks: orderBy((picks.items || []).map(game => ({ _id: game._id, game: game.game, pick: game.pick, rank: game.rank })), ['game'], ['asc']),
      }))
      if (result?.error || result?.payload?.messageType !== 'success') throw new Error(result?.payload?.message || 'We could not confirm your save. Your changes are still here; please try again.')
      setDirty(false)
      setSavedOnce(true)
    } catch (error) {
      setSaveError(error.message || 'We could not confirm your save. Your changes are still here; please try again.')
    }
    saving.current = false
    setSaving(false)
  }
  const rankedGames = orderBy(picks.items || [], ['rank'], ['desc'])
  return (
    <div className={`${s.container} ${s.regularEditor}`}>
      <header className={s.editorHeading}>
        <div>
          <p className={s.eyebrow}>{seasonName} · Regular season</p>
          <h1>Week {week} picks</h1>
          <p>{myTeam?.teamName || 'Your team'}</p>
        </div>
        {myTeams.length > 1 ? <label className={s.teamSelector}>Pick for
          <select value={pickTeamId || ''} disabled={isSaving || isLoading}
            onChange={event => { if (mayLeave()) dispatch(setPickTeamId(event.target.value)) }}>
            {myTeams.map(team => <option key={team._id} value={team._id}>{team.teamName}</option>)}
          </select>
        </label> : null}
      </header>
      {!myTeams.length ? <Alert variant='info'>No registered teams are available for this season.</Alert> :
        isLoading ? <div className={s.loading} role='status'><Spinner size='sm' /> Loading your picks…</div> :
          loadError ? <Alert variant='danger'>{loadError} <Button variant='outline-danger' onClick={() => { setLoadError(''); setAttempt(value => value + 1) }}>Try again</Button></Alert> : <>
            <div className={s.instructions}>
              <div><strong>Pick a team, then rank your confidence.</strong><br />Drag the handle or click it to choose points. Put your most confident picks first.</div>
              <div className={s.deadline}>
                {afterDeadline ? 'Picks are locked' : !deadlineValid ? 'Pick deadline unavailable' : `Due ${moment(deadline).from(moment(now))}`}
                {deadlineValid ? <small>{moment(deadline).format('ddd, MMM D · h:mm A z')}</small> : null}
              </div>
            </div>
            {afterDeadline ? <Alert variant='warning'>The deadline has passed. Picks can no longer be changed.</Alert> : null}
            <div className={s.columnLabels} aria-hidden='true'><span>Points</span><span>Favorite</span><span>Spread</span><span>Underdog</span></div>
            <ul className={s.picksContainer} aria-label='Games ranked by confidence'>
              {rankedGames.map(game => <EnterPicksGameContainer rank={game.rank} key={game.game}>
                <EnterPicksGameItem game={game} ranks={rankedGames.map(item => item.rank)}
                  seasonName={seasonName} disabled={!canEdit}
                  pickGameData={pickGameData.find(item => item.seasonName === seasonName && item.week === week && item.game === game.game)}
                  addCommand={addCommand} />
              </EnterPicksGameContainer>)}
            </ul>
            {!rankedGames.length ? <Alert variant='info'>No games are available for this week yet.</Alert> : null}
          </>}
      <footer className={s.saveBar}>
        <div aria-live='polite'>
          <strong>{isSaving ? 'Saving picks…' : dirty ? 'Unsaved changes' : isSavedOnce ? 'Your picks are saved' : 'Review your picks'}</strong>
          <small>{saveError || (dirty ? 'Save when you finish selecting and ranking.' : 'Selections and points are saved together.')}</small>
          {saveError ? <span className={s.saveError} role='alert'>Your changes have not been confirmed as saved.</span> : null}
        </div>
        <div className={s.saveActions}>
          {isSavedOnce && !dirty && myGame ? <Button variant='outline-success' onClick={() => navigate(`/football/regular-season/match-up/${seasonName}/week/${week}/game/${myGame.game}/${encodeURIComponent(myGame.name1 || '')}-vs-${encodeURIComponent(myGame.name2 || '')}`)}>View matchup</Button> : null}
          <Button variant='light' disabled={isSaving} onClick={() => { if (mayLeave()) navigate(`/football/regular-season/${seasonName}`) }}>Back</Button>
          <Button variant='success' disabled={!canEdit || !dirty || !rankedGames.length} onClick={savePicks}>
            {isSaving ? <Spinner size='sm' /> : null} {isSaving ? 'Saving…' : 'Save picks'}
          </Button>
        </div>
      </footer>
    </div>
  )
}

export default EnterRegularSeasonFootballPicks
